import React, { useState, useEffect, useContext, useRef } from "react";
import Draggable from "react-draggable";

import { useHistory } from "react-router-dom";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { Images } from "../../assets/common";
import Loader from "../Loader";
import ApiService from "../../services/api-service";
import { fullName, getErrorString } from "../../constants/function";
import notifier from "../Notifier";
import SuccessPopup from "../SuccessPopup";
import { SocketContext } from "../../context/socket";
import { useTranslation } from "react-i18next";


import "./index.scss";

export default function BiddingPopup(props) {
  const { t } = useTranslation("common");
  const history = useHistory();
  const socket = useContext(SocketContext).Socket;

  const [data, setData] = useState([]);
  const [userBids, setUserBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("siki");
  const [amount, setAmount] = useState("");
  const [highestBid, setHighestBid] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const artist = props?.nft?.artistData?.[0] || {}; // Get the first artist from artistData
  const bidsContainerRef = useRef(null); // Ref for the bids container
  



  console.log("Props in BiddingPopup:", props);

  // Fetch auction and user bid data
  const fetchData = async () => {
    if (!props?.nft?.nftId) {
      console.error("nftId is not defined in props:", props.nft);
      return;
    }

    try {
      setLoading(true);
      const [auctionResponse, bidResponse] = await Promise.all([
        ApiService.AUCTION_LIST(props.nft.nftId),
        ApiService.BID_LIST(props.nft.nftId),
      ]);

      const auctionBids = auctionResponse?.result || [];
      const userSpecificBids = bidResponse?.result || [];

      setData(auctionBids.reverse()); // Reverse to show newest bids at the top
      setUserBids(userSpecificBids); // Bids specific to the current user

      if (auctionBids.length > 0) {
        setHighestBid(Math.max(...auctionBids.map((bid) => bid.amount)));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [props.nft?.nftId]);

  // Scroll to the latest bid when the data updates
  useEffect(() => {
    if (bidsContainerRef.current) {
      bidsContainerRef.current.scrollTop = bidsContainerRef.current.scrollHeight; // Scroll to the bottom
    }
  }, [data]);

  useEffect(() => {
    const auctionId = props?.nft?.auctionId;
  
    if (!auctionId) {
      console.error("Auction ID is undefined during listener setup. Props:", props);
      return;
    }
  
    const handleRealTimeBidding = async (realtimeBid) => {
      console.log("Real-time bid received:", realtimeBid);
  
      if (realtimeBid.auctionId === auctionId) {
        console.log("Matching auction ID found. Reloading auction bids...");
        await fetchData(); // Reload the auction bids from the server
      } else {
        console.log("Auction ID mismatch:", {
          expected: auctionId,
          received: realtimeBid.auctionId,
        });
      }
    };
  
    const handleConnectionStatus = (status) => {
      console.log(`WebSocket ${status}`);
      if (status === "connected") {
        socket.on("realTimeBidding", handleRealTimeBidding);
      }
    };
  
    const handleError = (error) => {
      console.error("WebSocket error:", error);
      notifier.error("WebSocket connection error. Please try again.");
    };
  
    console.log("Attaching WebSocket listeners for auctionId:", auctionId);
    socket.on("realTimeBidding", handleRealTimeBidding);
    socket.on("connect", () => handleConnectionStatus("connected"));
    socket.on("disconnect", () => handleConnectionStatus("disconnected"));
    socket.on("error", handleError);
  
    return () => {
      console.log("Cleaning up WebSocket listeners for auctionId:", auctionId);
      socket.off("realTimeBidding", handleRealTimeBidding);
      socket.off("connect");
      socket.off("disconnect");
      socket.off("error", handleError);
    };
  }, [socket, props.nft?.auctionId]);
  
  

  // Place a bid
  const bid = async (auctionId, amountToBid) => {
    if (!auctionId) {
      notifier.error("No auction ID available for this bid.");
      return;
    }

    if (amount < amountToBid) {
      notifier.info("Bid amount cannot be less than the highest bid");
      return;
    }

    try {
      const payload = {
        auctionId,
        amount,
        currency,
        type: "music",
      };
      setLoading(true);

      const response = await ApiService.BID_NFT_V2(payload);
      notifier.success("Your bid has been successfully placed!");

      await fetchData(); // Reload the auction bids after a successful bid
      setShowSuccess(true);
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Draggable handle=".drag-handle">

    <Box className="_bidding">
      {/* Header */}
      <Stack spacing={1} direction="row" className="_bidding_header drag-handle">
        <img
          className="avatar"
          src={props?.nft?.details?.thumbnailImg?.filePath || props?.nft?.thumbnailImg || Images.default_user}
          alt="owner"
        />
        <Stack direction="column">
          <Typography className="fs-16 text-white">{props?.nft?.details?.title || props?.nft?.title }</Typography>
          <Typography
            className="fs-14 text-white _cp"
            onClick={() => {
              history.push(`/app/ArtistProfile/${props?.nft?.nftDetails?.owner?._id}`);
              props.handleClose(false);
            }}
          >
            {fullName(props?.nft?.nftDetails?.owner || artist?.fullName)}
          </Typography>
        </Stack>
        <CloseIcon className="_cp ml_auto" onClick={() => props.handleClose()} />
      </Stack>
      <Divider orientation="horizontal" />

      {/* Auction Bids */}
      <Typography variant="h6" className="fs-16 text-white">Auction Bids</Typography>
      <Stack id="__auction_bidding_data" className="_bidding_body" spacing={1}>
        {data.map((bidInfo, index) => (
          <Stack key={index} direction="row">
            <img
              className="avatar-sm"
              src={bidInfo?.userDetail?.profileImg?.filePath || Images.default_user}
              alt="Bidding"
            />
            <Stack direction="column">
              <Typography className="fs-14 text-white">
                {fullName(bidInfo?.userDetail)} - {bidInfo?.amount || 0}
              </Typography>
              <Typography className="fs-16 canceled">Rank: {bidInfo?.rank || "-"}</Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>

      {/* Bid Input */}
      <Stack className="_bidding_input">
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="_bidding_input_box"
          type="number"
          placeholder={`Must bid > ${highestBid.toFixed(2)}`}
        />
        <Button
          disabled={!amount}
          onClick={() => bid(data[0]?.auctionId || props.nft.auctionId, parseFloat(amount))}
        >
          {t("biddingPopup.button1")}
        </Button>
      </Stack>

      {showSuccess && (
        <SuccessPopup open={showSuccess} handleClose={() => setShowSuccess(false)} />
      )}
      {loading && <Loader wait={true} message={t("loader.message8")} />}
    </Box>
    </Draggable>

  );
}
